import type { AgentRunRow } from '../../lib/database.types'
import { isSameLocalDay } from '../../lib/time'

/**
 * cost_usd is authoritative. Nothing in this file derives money from tokens.
 *
 * The daily and monthly envelopes the spec draws ($120, $2,400) are in no table,
 * so they are not here either: the view renders spend and omits the envelope
 * rather than inventing a denominator.
 */

export interface DeptCost {
  department: string
  runs: number
  outputTokens: number
  cacheReadTokens: number
  spend: number
  perRun: number
}

export interface CostSummary {
  todaySpend: number
  todayRuns: number
  todayOutputTokens: number
  totalSpend: number
  byDepartment: DeptCost[]
  topRuns: AgentRunRow[]
  hasAnyCost: boolean
}

export function summarise(runs: ReadonlyArray<AgentRunRow>, now = Date.now()): CostSummary {
  const byDept = new Map<string, DeptCost>()
  let todaySpend = 0
  let todayRuns = 0
  let todayOutputTokens = 0
  let totalSpend = 0
  let hasAnyCost = false

  for (const run of runs) {
    const cost = run.cost_usd == null ? 0 : Number(run.cost_usd)
    const spend = Number.isFinite(cost) ? cost : 0
    if (run.cost_usd != null) hasAnyCost = true
    totalSpend += spend

    if (isSameLocalDay(run.created_at, now)) {
      todaySpend += spend
      todayRuns += 1
      todayOutputTokens += run.output_tokens ?? 0
    }

    const row = byDept.get(run.department) ?? {
      department: run.department, runs: 0, outputTokens: 0, cacheReadTokens: 0, spend: 0, perRun: 0,
    }
    row.runs += 1
    row.outputTokens += run.output_tokens ?? 0
    row.cacheReadTokens += run.cache_read_input_tokens ?? 0
    row.spend += spend
    byDept.set(run.department, row)
  }

  const byDepartment = [...byDept.values()]
    .map((r) => ({ ...r, perRun: r.runs ? r.spend / r.runs : 0 }))
    .sort((a, b) => b.spend - a.spend || a.department.localeCompare(b.department))

  const topRuns = [...runs]
    .filter((r) => r.cost_usd != null)
    .sort((a, b) => Number(b.cost_usd) - Number(a.cost_usd))
    .slice(0, 5)

  return { todaySpend, todayRuns, todayOutputTokens, totalSpend, byDepartment, topRuns, hasAnyCost }
}
